import { useEffect, useState } from "react";
import { IPaginationPayload } from "../../../interfaces/shared/IPaginationPayload";
import { useGetAllReservationsQuery, useGetGuestDetailsInfoMutation } from "../../../store/reservations/reservationsApi";
import ReservationDetailsModal from "./modals/ReservationDetailsModal";
import GuestInfoModal from "./modals/GuestInfoModal";
import ReservationsEmails from "./tables/ReservationsEmails";
import ReservationsTable from "./tables/ReservationsTable";
import Datepicker from "../../../shared/datepicker";
import Button from "../../../shared/button";
import Input from "../../../shared/input";

const Reservations = () => {
    const [showGuestModal, setShowGuestModal] = useState<{ show: boolean, data: any, reservationId?: string }>();
    const [showDetailsModal, setShowDetailsModal] = useState<{ show: boolean, data: any }>();
    const [search, setSearch] = useState<any>();
    const [searchReservation, setSearchReservation] = useState<string>();
    const [dateFilters, setDateFilters] = useState<any>();
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const { data, isLoading } = useGetAllReservationsQuery({ ...paginationState, data: { search: searchReservation, dateRange: dateFilters } });
    const [getGuestDetailsInfo, { isLoading: isLoadingGuestInfo }] = useGetGuestDetailsInfoMutation();

    const onClickShowGuestInfo = (data: any, reservationId?: string) => {
        setShowGuestModal({
            show: true,
            data: data,
            reservationId: reservationId
        })
        document.body.style.overflow = 'hidden';
    }

    const onCloseModal = () => {
        setShowGuestModal(undefined)
        setShowDetailsModal(undefined)
        document.body.style.overflow = 'auto';
    }

    const onSeeReservationDetails = (data: any) => {
        setShowDetailsModal({
            show: true,
            data: data
        })
        document.body.style.overflow = 'hidden';
    }

    const onDownloadInvoice = (invoice: string) => {
        window.open(invoice, '_blank')
    }

    const onChangeSearchValue = (e: any) => {
        setSearch(e)
    }

    const onClickSearchProfile = async () => {
        if (search && search !== '') {
            try {
                const response: any = await getGuestDetailsInfo(search).unwrap();
                onClickShowGuestInfo(response?.data)
                setSearch(undefined)
            } catch (err) {
                // err here
            }
        }
    }

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        })
    }

    const onChangeSearchReservationValue = (e: any) => {
        setSearchReservation(e)
        onResetPaginationState()
    }

    const onChangeSelectedFilter = (e: any, type: string) => {
        setDateFilters((prevFilters: any) => ({
            ...prevFilters,
            [type]: e || undefined
        }))
    }

    const onClearFilters = () => {
        setSearchReservation(undefined)
        setDateFilters(undefined)
    }

    return (
        <div className="flex flex-wrap">
            <div className="w-full lg:w-8/12 xl:w-9/12">
                <h2 className="page-title">Reservations</h2>
                <div className={`flex flex-row justify-between items-center bg-[#E8F1FD] mt-3 mb-10 p-1 !rounded`}>
                    <div>
                        <Input
                            placeholder={'Search Guest Info Details'}
                            dataQa={'search'}
                            containerStyle='mb-0 pb-0 relative min-w-[400px]'
                            inputStyle={`mb-0 border-0 focus:outline-none`}
                            icon={'/assets/shared/search.svg'}
                            showValue={true}
                            inputValue={search}
                            disabled={!!isLoadingGuestInfo}
                            inputUniqueName={'search'}
                            onChangeInput={onChangeSearchValue}
                            hideInputSpace={true}
                        />
                    </div>
                    <div className='flex flex-row justify-end'>
                        <Button
                            label={isLoadingGuestInfo ? 'Loading Data...' : 'Search Guest Details'}
                            className={`${!isLoadingGuestInfo ? 'btn-primary-rounded-less' : 'btn-primary-rounded-less-disable cursor-not-allowed'} min-w-auto`}
                            onClickButton={() => onClickSearchProfile()}
                        />
                    </div>
                </div>
                <div>
                    <p className='text-primary-light font-bold text-sm mt-2 mb-4'>Date Filters</p>
                    <div className='mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1 sm:gap-2 lg:gap-4'>
                        <Datepicker
                            placeholder='Check In'
                            containerStyle='mb-0'
                            inputValue={dateFilters?.checkIn}
                            onChangeInput={onChangeSelectedFilter}
                            inputUniqueName={'checkIn'}
                        />
                        <Datepicker
                            placeholder='Check Out'
                            containerStyle='mb-0'
                            inputValue={dateFilters?.checkOut}
                            onChangeInput={onChangeSelectedFilter}
                            inputUniqueName={'checkOut'}
                        />
                    </div>
                </div>
                <div className={`flex flex-row justify-between items-center bg-call-default mt-3 p-1 !rounded`}>
                    <Input
                        placeholder={'Search for Reservation'}
                        dataQa={'search'}
                        containerStyle='mb-0 pb-0 relative min-w-[80%]'
                        inputStyle={`mb-0 border-0 focus:outline-none`}
                        icon={'/assets/shared/search.svg'}
                        showValue={true}
                        inputValue={searchReservation}
                        inputUniqueName={'search'}
                        onChangeInput={onChangeSearchReservationValue}
                        hideInputSpace={true}
                    />
                    <Button
                        label={'Clear all filters'}
                        className="btn-primary-text-underline"
                        onClickButton={onClearFilters}
                    />
                </div>
                <ReservationsTable
                    rows={data?.data?.elements || []}
                    paginationData={data?.data.page}
                    rowsLoading={isLoading}
                    onSeeReservationDetails={onSeeReservationDetails}
                    setPaginationState={setPaginationState}
                    onClickShowGuestInfo={onClickShowGuestInfo}
                    onDownloadInvoice={onDownloadInvoice}
                />
            </div>
            <div className="w-full lg:w-4/12 xl:w-3/12 px-4">
                <ReservationsEmails />
            </div>
            {showGuestModal?.show &&
                <GuestInfoModal
                    openModal={showGuestModal?.show}
                    data={showGuestModal?.data || ''}
                    reservationId={showGuestModal?.reservationId}
                    handleCloseModal={onCloseModal}
                />
            }
            {showDetailsModal?.show &&
                <ReservationDetailsModal
                    openModal={showDetailsModal?.show}
                    data={showDetailsModal?.data}
                    handleCloseModal={onCloseModal}
                />
            }
        </div>
    );
};

export default Reservations;
